import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { AlertCircle, Plus } from 'lucide-react';

import { normalizePhoneNumber } from '@/shared/lib/phones';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { useLeadByPhone } from '@/entities/lead';
import { useShowDialog } from '@/hooks/useShowDialog';
import { LeadCard } from './LeadCard';
import { LeadCalls } from './LeadCalls';
import  { type LeadData } from './types';

type SearchStateType = 'ready' | 'loading' | 'notfound' | 'view'

export function LeadSearchPage() {
  const location = useLocation()
  const locationState = location.state as { phone_number?: string } | null

  const [isFirst, setIsFirst] = useState(true)
  const [isNew, setIsNew] = useState(false)
  const [searchState, setSearchState] = useState<SearchStateType>('ready')
  const [phone, setPhone] = useState(locationState?.phone_number || '')
  const [searchPhone, setSearchPhone] = useState<string | null>(null)
  const [lead, setLead] = useState<LeadData | null>(null)

  const [hasChanges, setHasChanges] = useState(false)

  useShowDialog(hasChanges)

  const { data: leadData, isLoading, isError, refetch } = useLeadByPhone(searchPhone)

  // Reset all state
  const resetAllState = useCallback((fromSearch = false) => {
    setIsNew(false)
    if (!fromSearch) {
      setPhone('')
      setSearchPhone(null)
    }
    setLead(null)
    setHasChanges(false)
  }, [])

  // Check phone from state if available (on first load)
  useEffect(() => {
    if (isFirst) {
      if (locationState?.phone_number) {
        const normalizedPhone = normalizePhoneNumber(locationState.phone_number)
        if (normalizedPhone.length >= 10) {
          setSearchPhone(normalizedPhone)
        }
      }
      setIsFirst(false)
    }
  }, [isFirst, locationState])

  // Update state based on query result
  useEffect(() => {
    if (isLoading) {
      if (!lead) setSearchState('loading')
      return
    }

    if (searchPhone === null) {
      return
    }

    if (isError) {
      setSearchState('notfound')
      setLead(null)
      return
    }

    if (leadData) {
      setSearchState('view')
      setLead(leadData)
    }
  }, [leadData, isLoading, isError, searchPhone])

  const handleSearch = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Do you want to discard them?')) return
      resetAllState(true)
    }
    setIsNew(false)
    setHasChanges(false)
    const normalizedPhone = normalizePhoneNumber(phone)

    if (normalizedPhone.length >= 10) {
      if (normalizedPhone === searchPhone) {
        refetch()
      } else {
        setLead(null)
        setSearchPhone(normalizedPhone)
      }
    }
  }

  const handleAddNew = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Do you want to discard them?')) return
      resetAllState()
    }
    setIsNew(true)
    setSearchState('view')
    setPhone('')
    setSearchPhone(null)
    setLead(null)
    setHasChanges(false)
  }

  const handleSaveSuccess = () => {
    // After successful save, refetch the lead data
    if (searchPhone) {
      refetch()
    }
  }

  return (
    <div className="container py-4 space-y-4">
      <h1 className="text-heading font-bold">Lead Search</h1>

      <div className="flex gap-2 justify-between items-center">
        <div className="flex gap-2 max-w-[600px] flex-1">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Enter phone number (e.g. 661-835-8023)"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <Button
            onClick={handleSearch}
            disabled={!phone || normalizePhoneNumber(phone).length < 10}
          >
            Search
          </Button>
        </div>

        <Button className="w-full sm:w-auto" onClick={handleAddNew} disabled={isNew}>
          <Plus className="w-4 h-4 mr-2" />
          New Lead
        </Button>
      </div>

      {searchState === 'notfound' && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Lead Not Found</AlertTitle>
          <AlertDescription>No lead found with this phone number</AlertDescription>
        </Alert>
      )}

      <Card>
        {searchState === 'view' ? (
          <LeadCard
            hasChanges={hasChanges}
            isNew={isNew}
            lead={lead}
            setHasChanges={setHasChanges}
            setIsNew={setIsNew}
            onSaveSuccess={handleSaveSuccess}
            onDeleteSuccess={() => {
              resetAllState()
              setSearchState('ready')
            }}
          />
        ) : (
          <CardContent className="flex place-self-center items-center pt-6 text-center text-body text-gray-500">
            {searchState === 'ready' && 'Search lead by phone number first'}
            {searchState === 'loading' && 'Loading...'}
            {searchState === 'notfound' && 'No lead data found.'}
          </CardContent>
        )}
      </Card>

      {/* Call History - only show when viewing an existing lead */}
      {searchState === 'view' && lead && <LeadCalls phone={lead.phone_number || ''} />}
    </div>
  )
}